// =============================================================================
// Admin Documents Management Page
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { adminService, type AdminDocument, type DealWithStats, type InvestorWithProfiles } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { DocumentViewerModal } from '../../components/ui';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import type { DocumentType } from '../../types';

// =============================================================================
// Types
// =============================================================================

type VisibilityMode = 'deal' | 'specific';

// =============================================================================
// Component
// =============================================================================

export function DocumentsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canUpload = hasPermission('documents', 'upload');
  const canEdit = hasPermission('documents', 'edit');
  const canDelete = hasPermission('documents', 'delete');
  const [searchParams] = useSearchParams();
  const initialDealId = searchParams.get('dealId') || '';

  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(initialDealId);
  const [selectedType, setSelectedType] = useState<DocumentType | ''>('');
  const [selectedInvestor, setSelectedInvestor] = useState('');
  const [allInvestors, setAllInvestors] = useState<InvestorWithProfiles[]>([]);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDealId, setUploadDealId] = useState('');
  const [uploadType, setUploadType] = useState<DocumentType>('OTHER');
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [notifyInvestors, setNotifyInvestors] = useState(false);

  // Visibility controls
  const [visibilityMode, setVisibilityMode] = useState<VisibilityMode>('deal');
  const [dealInvestors, setDealInvestors] = useState<InvestorWithProfiles[]>([]);
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<Set<string>>(new Set());
  const [loadingInvestors, setLoadingInvestors] = useState(false);

  // Document viewer
  const [viewingDocument, setViewingDocument] = useState<{ id: string; name: string; mimeType?: string } | null>(null);

  // Edit modal
  const [editingDocument, setEditingDocument] = useState<AdminDocument | null>(null);
  const [editType, setEditType] = useState<DocumentType>('OTHER');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Edit visibility
  const [editVisibilityMode, setEditVisibilityMode] = useState<VisibilityMode>('deal');
  const [editDealInvestors, setEditDealInvestors] = useState<InvestorWithProfiles[]>([]);
  const [editSelectedInvestorIds, setEditSelectedInvestorIds] = useState<Set<string>>(new Set());
  const [editLoadingInvestors, setEditLoadingInvestors] = useState(false);
  const [originalVisibilityMode, setOriginalVisibilityMode] = useState<VisibilityMode>('deal');
  const [originalSelectedInvestorIds, setOriginalSelectedInvestorIds] = useState<Set<string>>(new Set());

  // Visibility change confirmation
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [visibilityChangeSummary, setVisibilityChangeSummary] = useState<{ added: string[]; removed: string[] }>({ added: [], removed: [] });

  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const documentTypes: { value: DocumentType; label: string }[] = [
    { value: 'FINANCIAL_STATEMENT', label: 'Tax / Financial Statement' },
    { value: 'QUARTERLY_REPORT', label: 'Quarterly Report' },
    { value: 'K1', label: 'K-1' },
    { value: 'PPM', label: 'PPM' },
    { value: 'SUBSCRIPTION_AGREEMENT', label: 'Subscription Agreement' },
    { value: 'OPERATING_AGREEMENT', label: 'Operating Agreement' },
    { value: 'OTHER', label: 'Other' },
  ];

  useEffect(() => {
    loadDeals();
    loadAllInvestors();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [selectedDeal, selectedType, searchQuery, selectedInvestor]);

  // Load investors when deal is selected in upload form
  useEffect(() => {
    if (uploadDealId && visibilityMode === 'specific') {
      loadDealInvestors(uploadDealId);
    }
  }, [uploadDealId, visibilityMode]);

  const loadDeals = async () => {
    try {
      const data = await adminService.listDeals();
      setDeals(data);
    } catch (err) {
      console.error('Failed to load deals:', err);
    }
  };

  const loadAllInvestors = async () => {
    try {
      const result = await adminService.listInvestors({ limit: 1000 });
      setAllInvestors(result.investors);
    } catch (err) {
      console.error('Failed to load investors:', err);
    }
  };

  const loadDealInvestors = async (dealId: string) => {
    setLoadingInvestors(true);
    try {
      const result = await adminService.listInvestors({ dealId });
      setDealInvestors(result.investors);
      // By default, select all investors when loading
      setSelectedInvestorIds(new Set(result.investors.map(inv => inv.id)));
    } catch (err) {
      console.error('Failed to load investors:', err);
    } finally {
      setLoadingInvestors(false);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const result = await adminService.listDocuments({
        dealId: selectedDeal || undefined,
        type: selectedType as DocumentType || undefined,
        search: searchQuery || undefined,
        investorId: selectedInvestor || undefined,
        limit: 100,
      });
      setDocuments(result.documents);
      setTotal(result.total);
    } catch (err) {
      setError('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadName) {
        setUploadName(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadDealId || !uploadType || !uploadName) {
      setUploadError('Please fill in all required fields');
      return;
    }

    // Validate specific visibility mode
    if (visibilityMode === 'specific' && selectedInvestorIds.size === 0) {
      setUploadError('Please select at least one investor');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      await adminService.uploadDocument(uploadFile, {
        dealId: uploadDealId,
        type: uploadType,
        name: uploadName,
        description: uploadDescription || undefined,
        notifyInvestors,
        visibilityMode,
        selectedInvestorIds: visibilityMode === 'specific' ? Array.from(selectedInvestorIds) : undefined,
      });

      // Reset form and close modal
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } catch (err: unknown) {
      console.error('Document upload error (full):', err);
      // Try multiple ways to get the error message
      const axiosErr = err as { response?: { data?: { error?: string }; status?: number }; message?: string };
      const errorMsg = axiosErr.response?.data?.error
        || getApiError(err).message
        || axiosErr.message
        || 'Upload failed - please try again';
      console.error('Error message extracted:', errorMsg);
      setUploadError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadDealId('');
    setUploadType('OTHER');
    setUploadName('');
    setUploadDescription('');
    setNotifyInvestors(false);
    setUploadError(null);
    setVisibilityMode('deal');
    setDealInvestors([]);
    setSelectedInvestorIds(new Set());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleInvestor = (investorId: string) => {
    setSelectedInvestorIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(investorId)) {
        newSet.delete(investorId);
      } else {
        newSet.add(investorId);
      }
      return newSet;
    });
  };

  const selectAllInvestors = () => {
    setSelectedInvestorIds(new Set(dealInvestors.map(inv => inv.id)));
  };

  const deselectAllInvestors = () => {
    setSelectedInvestorIds(new Set());
  };

  const handleDeleteClick = (documentId: string, documentName: string) => {
    setDeleteTarget({ id: documentId, name: documentName });
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleDownload = async (documentId: string, documentName: string) => {
    try {
      const blob = await adminService.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(getApiError(err).message || 'Failed to download document');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmText.toLowerCase() !== 'proceed') return;

    try {
      setDeleting(true);
      await adminService.deleteDocument(deleteTarget.id);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (doc: AdminDocument) => {
    setEditingDocument(doc);
    setEditType(doc.type as DocumentType);
    setEditName(doc.name);
    setEditDescription(doc.description || '');
    setEditError(null);

    // Determine current visibility from accessLevels
    const userLevels = (doc.accessLevels || []).filter(l => l.startsWith('user:'));
    if (userLevels.length > 0) {
      const ids = new Set(userLevels.map(l => l.replace('user:', '')));
      setEditVisibilityMode('specific');
      setEditSelectedInvestorIds(ids);
      setOriginalVisibilityMode('specific');
      setOriginalSelectedInvestorIds(new Set(ids));
    } else {
      setEditVisibilityMode('deal');
      setEditSelectedInvestorIds(new Set());
      setOriginalVisibilityMode('deal');
      setOriginalSelectedInvestorIds(new Set());
    }

    // Load deal investors for this document's deal
    if (doc.deal?.id) {
      loadEditDealInvestors(doc.deal.id);
    }
  };

  const loadEditDealInvestors = async (dealId: string) => {
    setEditLoadingInvestors(true);
    try {
      const result = await adminService.listInvestors({ dealId });
      setEditDealInvestors(result.investors);
    } catch (err) {
      console.error('Failed to load deal investors for edit:', err);
    } finally {
      setEditLoadingInvestors(false);
    }
  };

  // Check if visibility has changed
  const hasVisibilityChanged = (): boolean => {
    if (editVisibilityMode !== originalVisibilityMode) return true;
    if (editVisibilityMode === 'specific') {
      if (editSelectedInvestorIds.size !== originalSelectedInvestorIds.size) return true;
      for (const id of editSelectedInvestorIds) {
        if (!originalSelectedInvestorIds.has(id)) return true;
      }
    }
    return false;
  };

  // Get investor name by ID from editDealInvestors
  const getInvestorName = (userId: string): string => {
    const inv = editDealInvestors.find(i => i.id === userId);
    return inv ? inv.name : userId;
  };

  // Compute visibility change summary
  const computeVisibilityChanges = (): { added: string[]; removed: string[] } => {
    const added: string[] = [];
    const removed: string[] = [];

    if (originalVisibilityMode === 'deal' && editVisibilityMode === 'specific') {
      // Changing from all investors to specific - those NOT selected are being removed
      for (const inv of editDealInvestors) {
        if (editSelectedInvestorIds.has(inv.id)) {
          added.push(inv.name);
        }
      }
      return { added: [], removed: [`All deal investors (now restricted to ${added.length} specific investor${added.length !== 1 ? 's' : ''})`] };
    }

    if (originalVisibilityMode === 'specific' && editVisibilityMode === 'deal') {
      return { added: ['All investors in this deal'], removed: [] };
    }

    // Both specific - compute diff
    for (const id of editSelectedInvestorIds) {
      if (!originalSelectedInvestorIds.has(id)) {
        added.push(getInvestorName(id));
      }
    }
    for (const id of originalSelectedInvestorIds) {
      if (!editSelectedInvestorIds.has(id)) {
        removed.push(getInvestorName(id));
      }
    }

    return { added, removed };
  };

  const handleEditSave = async () => {
    if (!editingDocument || !editName.trim()) return;

    // Check if visibility changed - require confirmation
    if (hasVisibilityChanged()) {
      const changes = computeVisibilityChanges();
      setVisibilityChangeSummary(changes);
      setConfirmText('');
      setShowVisibilityConfirm(true);
      return;
    }

    await performEditSave();
  };

  const performEditSave = async () => {
    if (!editingDocument || !editName.trim()) return;

    try {
      setEditSaving(true);
      setEditError(null);

      const updateData: { name: string; description?: string; type: DocumentType; visibilityMode?: string; selectedInvestorIds?: string[] } = {
        type: editType,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      };

      // Include visibility if changed
      if (hasVisibilityChanged()) {
        updateData.visibilityMode = editVisibilityMode;
        if (editVisibilityMode === 'specific') {
          updateData.selectedInvestorIds = Array.from(editSelectedInvestorIds);
        }
      }

      await adminService.updateDocument(editingDocument.id, updateData);
      setEditingDocument(null);
      setShowVisibilityConfirm(false);
      loadDocuments();
    } catch (err) {
      const errorMsg = getApiError(err).message || 'Failed to update document';
      setEditError(errorMsg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleVisibilityConfirmProceed = () => {
    if (confirmText.toLowerCase() !== 'proceed') return;
    setShowVisibilityConfirm(false);
    performEditSave();
  };

  const editToggleInvestor = (investorId: string) => {
    setEditSelectedInvestorIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(investorId)) {
        newSet.delete(investorId);
      } else {
        newSet.add(investorId);
      }
      return newSet;
    });
  };

  const editSelectAllInvestors = () => {
    setEditSelectedInvestorIds(new Set(editDealInvestors.map(inv => inv.id)));
  };

  const editDeselectAllInvestors = () => {
    setEditSelectedInvestorIds(new Set());
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getDocumentTypeLabel = (type: string) => {
    const found = documentTypes.find((t) => t.value === type);
    return found?.label || type;
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('documents', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">Manage and organize investor documents</p>
        </div>
        {canUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Document
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal</label>
            <select
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Deals</option>
              {deals.map((deal) => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Investor</label>
            <select
              value={selectedInvestor}
              onChange={(e) => setSelectedInvestor(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Investors</option>
              {allInvestors.map((investor) => (
                <option key={investor.id} value={investor.id}>
                  {investor.name} ({investor.email})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as DocumentType | '')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedDeal('');
                setSelectedType('');
                setSelectedInvestor('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500">No documents found</p>
            {canUpload && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="mt-4 text-primary-600 hover:text-primary-800"
              >
                Upload your first document
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="px-6 py-3 border-b bg-gray-50">
              <p className="text-sm text-gray-500">{total} document{total !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y">
              {documents.map((doc) => (
                <div key={doc.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div
                    className="flex items-center gap-4 flex-1 cursor-pointer"
                    onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 hover:text-primary-600">{doc.name}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                          {getDocumentTypeLabel(doc.type)}
                        </span>
                        {doc.deal && <span>{doc.deal.name}</span>}
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>{formatDate(doc.createdAt)}</span>
                      </div>
                      {doc.investorProfile ? (
                        <p className="text-sm text-gray-400 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Visible to: {doc.investorProfile.name}
                          </span>
                        </p>
                      ) : doc.visibleToCount !== undefined && doc.visibleToCount > 0 ? (
                        <p className="text-sm text-gray-400 mt-1">
                          <span className="inline-flex items-center gap-1">
                            {doc.visibleToCount === 1 && doc.visibleToNames && doc.visibleToNames.length > 0 ? (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                Visible to: {doc.visibleToNames[0]}
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                Visible to {doc.visibleToCount} investor{doc.visibleToCount !== 1 ? 's' : ''}
                              </>
                            )}
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Visible to all deal investors
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditModal(doc); }}
                        className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                        title="Edit document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(doc.id, doc.name); }}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                      title="Download document"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteClick(doc.id, doc.name)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Upload Document</h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {uploadError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {uploadError}
                </div>
              )}

              <div className="space-y-4">
                {/* File Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 ${
                      uploadFile ? 'border-primary-400 bg-primary-50' : 'border-gray-300'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.csv,.txt"
                    />
                    {uploadFile ? (
                      <div>
                        <svg className="w-8 h-8 text-primary-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="font-medium text-gray-900">{uploadFile.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(uploadFile.size)}</p>
                      </div>
                    ) : (
                      <div>
                        <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        <p className="text-gray-600">Click to select a file</p>
                        <p className="text-sm text-gray-400">PDF, Word, Excel, Images</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Deal Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal *</label>
                  <select
                    value={uploadDealId}
                    onChange={(e) => setUploadDealId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Select a deal</option>
                    {deals.map((deal) => (
                      <option key={deal.id} value={deal.id}>{deal.name}</option>
                    ))}
                  </select>
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                  <select
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value as DocumentType)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {documentTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Document Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Name *</label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="Enter document name"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Optional description"
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Visibility Controls */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Who can see this document?</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="visibility"
                        checked={visibilityMode === 'deal'}
                        onChange={() => setVisibilityMode('deal')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">All investors in this deal</span>
                        <p className="text-xs text-gray-500">Everyone who has invested in the selected deal can view this document</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="visibility"
                        checked={visibilityMode === 'specific'}
                        onChange={() => setVisibilityMode('specific')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Specific investors only</span>
                        <p className="text-xs text-gray-500">Select which investors can view this document</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Investor Selection (when specific mode) */}
                {visibilityMode === 'specific' && uploadDealId && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Select Investors</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={selectAllInvestors}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={deselectAllInvestors}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    {loadingInvestors ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                      </div>
                    ) : dealInvestors.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No investors found for this deal
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {dealInvestors.map((investor) => (
                          <label
                            key={investor.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedInvestorIds.has(investor.id)}
                              onChange={() => toggleInvestor(investor.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {investor.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {investor.email}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      {selectedInvestorIds.size} of {dealInvestors.length} investors selected
                    </p>
                  </div>
                )}

                {visibilityMode === 'specific' && !uploadDealId && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-500 text-center">
                      Please select a deal first to see available investors
                    </p>
                  </div>
                )}

                {/* Notify Investors */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notifyInvestors"
                    checked={notifyInvestors}
                    onChange={(e) => setNotifyInvestors(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="notifyInvestors" className="text-sm text-gray-700">
                    Notify selected investors by email
                  </label>
                </div>
              </div>

              {/* Bulk Upload Section */}
              <div className="mt-4 pt-4 border-t border-dashed border-gray-200 text-center">
                <p className="text-sm text-gray-500 mb-2">Need to upload multiple files?</p>
                <Link
                  to="/admin/documents/bulk-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Bulk Upload
                </Link>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    resetUploadForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile || !uploadDealId || !uploadName}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Uploading...
                    </>
                  ) : (
                    'Upload Document'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {editingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setEditingDocument(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Edit Document</h2>
                <button
                  onClick={() => setEditingDocument(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {editError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {editError}
                </div>
              )}

              <div className="space-y-4">
                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as DocumentType)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {documentTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Document Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Optional description"
                  />
                </div>

                {/* Visibility Controls */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Who can see this document?</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="editVisibility"
                        checked={editVisibilityMode === 'deal'}
                        onChange={() => setEditVisibilityMode('deal')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">All investors in this deal</span>
                        <p className="text-xs text-gray-500">Everyone who has invested in {editingDocument.deal?.name || 'this deal'} can view this document</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="editVisibility"
                        checked={editVisibilityMode === 'specific'}
                        onChange={() => setEditVisibilityMode('specific')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Specific investors only</span>
                        <p className="text-xs text-gray-500">Select which investors can view this document</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Investor Selection (when specific mode) */}
                {editVisibilityMode === 'specific' && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Select Investors</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={editSelectAllInvestors}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={editDeselectAllInvestors}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    {editLoadingInvestors ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                      </div>
                    ) : editDealInvestors.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No investors found for this deal
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {editDealInvestors.map((investor) => (
                          <label
                            key={investor.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={editSelectedInvestorIds.has(investor.id)}
                              onChange={() => editToggleInvestor(investor.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {investor.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {investor.email}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      {editSelectedInvestorIds.size} of {editDealInvestors.length} investors selected
                    </p>
                  </div>
                )}

                {/* Visibility change warning */}
                {hasVisibilityChanged() && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800">
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-sm font-medium">Visibility will be changed. You will be asked to confirm.</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setEditingDocument(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={editSaving || !editName.trim() || (editVisibilityMode === 'specific' && editSelectedInvestorIds.size === 0)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {editSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visibility Change Confirmation Modal */}
      {showVisibilityConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Visibility Change</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              You are about to change who can see this document. Please review the changes below carefully.
            </p>

            <div className="space-y-3 mb-4">
              {visibilityChangeSummary.removed.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-1">Removing access:</p>
                  <ul className="text-sm text-red-700 space-y-0.5">
                    {visibilityChangeSummary.removed.map((name, i) => (
                      <li key={i}>- {name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {visibilityChangeSummary.added.length > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-1">Adding access:</p>
                  <ul className="text-sm text-green-700 space-y-0.5">
                    {visibilityChangeSummary.added.map((name, i) => (
                      <li key={i}>+ {name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm this change:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowVisibilityConfirm(false);
                  setConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleVisibilityConfirmProceed}
                disabled={confirmText.toLowerCase() !== 'proceed' || editSaving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Saving...
                  </>
                ) : (
                  'Confirm Change'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Document</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this document:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {deleteTarget.name}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone. The document will be removed from the system and will no longer be accessible to any investors.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Document'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          getEmbedUrls={adminService.getDocumentViewUrl}
        />
      )}
    </div>
  );
}

export default DocumentsPage;
